// Wraps the medreview session JWT into a typed http client. All api calls
// in the SPA go through this. The session JWT comes from SessionProvider,
// which exchanges a Shopify App Bridge token for it at startup. The api
// middleware verifies the medreview-issued JWT and resolves req.user.
//
// On 401, useAuthedFetch refreshes the session via SessionProvider.refresh()
// once and retries; if that still 401s, it throws UnauthenticatedError so
// the page surfaces an "open from Shopify Admin" state.

import { useCallback } from 'react';
import { env } from '../env';
import { useSession } from './SessionProvider';

export type AuthedFetch = (path: string, init?: RequestInit) => Promise<Response>;

export class UnauthenticatedError extends Error {
  constructor() {
    super('Not authenticated: open MedReview from your Shopify Admin.');
    this.name = 'UnauthenticatedError';
  }
}

export function useAuthedFetch(): AuthedFetch {
  const { sessionToken, refresh } = useSession();
  return useCallback(
    async (path: string, init: RequestInit = {}) => {
      const url = path.startsWith('http') ? path : `${env.apiBaseUrl}${path}`;
      const send = async (token: string | null): Promise<Response> => {
        if (!token) throw new UnauthenticatedError();
        const headers = new Headers(init.headers);
        headers.set('Authorization', `Bearer ${token}`);
        if (!headers.has('Content-Type') && init.body) {
          headers.set('Content-Type', 'application/json');
        }
        return fetch(url, { ...init, headers });
      };

      let token = sessionToken;
      if (!token) {
        token = await refresh();
      }
      let res = await send(token);
      if (res.status === 401) {
        // Stale or rotated session; force a fresh exchange and retry once.
        const fresh = await refresh();
        if (!fresh) throw new UnauthenticatedError();
        res = await send(fresh);
      }
      return res;
    },
    [sessionToken, refresh],
  );
}
